declare global {
  interface Window {
    DD_RUM?: {
      addError: (err: Error, extraContext?: Record<string, any>) => void
    }
  }
}

const reportError = (err: Error, extraContext: Record<string, any> = {}) => {
  try {
    if (typeof window !== 'undefined' && window.DD_RUM && typeof window.DD_RUM.addError === 'function') {
      window.DD_RUM.addError(err, extraContext)
    }
  } catch (e) {
    // never let telemetry break the app
    console.warn('Error reporting to DD_RUM', e)
  }
}

const getCurrentRoute = (router: any): string => {
  try {
    // In Nuxt 3/Vue 3, currentRoute is a ref, so access .value
    if (router?.currentRoute?.value?.fullPath) {
      return router.currentRoute.value.fullPath
    }
    // Fallback for Vue 2 compatibility or if structure is different
    if (router?.currentRoute?.fullPath) {
      return router.currentRoute.fullPath
    }
    return window.location.pathname
  } catch {
    return window.location.pathname
  }
}

const attachWindowHandler = (router: any) => {
  window.addEventListener('error', (event) => {
    const err = event.error || new Error(event.message)

    reportError(err, {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      route: getCurrentRoute(router),
      url: window.location.href
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason || 'unhandledrejection'))

    reportError(err, {
      source: 'unhandledrejection',
      route: getCurrentRoute(router),
      url: window.location.href
    })
  })
}

const overwriteConsoleError = (router: any) => {
  const origConsoleError = console.error.bind(console)

  console.error = (...args: any[]) => {
    try {
      const errArg = args.find((a) => a instanceof Error)
      const err = errArg || new Error(args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' '))

      reportError(err, {
        source: 'console.error',
        console_args: args,
        route: getCurrentRoute(router),
        url: window.location.href
      })
    } catch (_) {
      // ignore
    }

    return origConsoleError(...args)
  }
}

const resolveComponentName = (component: any): string => {
  if (!component) {
    return 'anonymous'
  }

  // Vue 3 component structure
  if (component.type?.name) {
    return component.type.name
  }

  if (component.type?.__name) {
    return component.type.__name
  }

  // Fallback for Vue 2 compatibility or Options API
  if (component.$options?.name) {
    return component.$options.name
  }

  if (component.$options?._componentTag) {
    return component.$options._componentTag
  }

  return 'anonymous'
}

const attachVueListeners = (nuxtApp: any, router: any) => {
  nuxtApp.vueApp.config.errorHandler = (err: Error, instance: any, info: string) => {
    const componentName = resolveComponentName(instance)

    reportError(err, {
      vue_component: componentName,
      vue_info: info,
      route: getCurrentRoute(router),
      url: window.location.href,
      source: 'vue-error'
    })
    // force DD to send the error to the server
    window.dispatchEvent(new Event('pagehide'))
  }

  nuxtApp.vueApp.config.warnHandler = (msg: string, instance: any, trace: string) => {
    const componentName = resolveComponentName(instance)

    reportError(new Error(msg), {
      vue_component: componentName,
      vue_trace: trace,
      route: getCurrentRoute(router),
      url: window.location.href,
      source: 'vue-warn'
    })
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()

  attachVueListeners(nuxtApp, router)
  attachWindowHandler(router)
  overwriteConsoleError(router)
})
